// =============================================
// Publish Queue Service
// v3.1: Atomic claim loop (FOR UPDATE SKIP LOCKED via RPC) + heartbeat + lock cleanup
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { wordpressService } from "@/services/wordpressService";
import { isWorkerModeEnabled } from "@/lib/workerMode";
import { enqueuePublishJob } from "@/services/queueClient";
import {
  generateSchemasForNode,
  validateSchemaPayload,
  injectSchemasIntoHtml,
  logSchemaInjection,
} from "@/services/schema/schemaGenerator";
import { logPublic } from "@/services/contentEngine/contentLogger";
import { postContentAudit } from "@/services/contentEngine/postContentAudit";
import { stripPendingLinks } from "@/services/contentEngine/linkResolver";
import { healPendingLinksForNewPublish } from "@/services/healPendingLinks";

// Feature flag: lazy auto-QA backfill dla nodów z qa_status=pending podczas enqueue.
// Wyłącz przez VITE_AUTO_QA_ON_ENQUEUE=false + restart kontenera Coolify (bez deploy).
const AUTO_QA_ON_ENQUEUE_ENABLED =
  (import.meta.env.VITE_AUTO_QA_ON_ENQUEUE ?? "true") !== "false";

const PUBLISH_RATE_PER_DOMAIN = 2; // articles/day per domain
const PUBLISH_WINDOW_START = 6; // 6:00
const PUBLISH_WINDOW_END = 22; // 22:00
const MIN_DELAY_HOURS = 8;
const MAX_DELAY_HOURS = 16;
const MAX_RETRIES = 3;

// Lock lease length (minutes) — claim_publish_job RPC default; heartbeat extends it
const LOCK_LEASE_MINUTES = 10;
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 min

interface ClaimedPublishJob {
  id: string;
  user_id: string;
  map_id: string;
  node_id: string;
  website_id: string | null;
  scheduled_at: string | null;
  retry_count: number;
}

/**
 * Atomically claim a single publish job via RPC (FOR UPDATE SKIP LOCKED).
 * RPC honors scheduled_at <= NOW() gate — only due items are returned.
 * Returns null if no job available (queue empty / nothing due / all locked).
 */
async function claimNextPublishJob(sessionId: string): Promise<ClaimedPublishJob | null> {
  const { data, error } = await (supabase as any).rpc("claim_publish_job", {
    p_session_id: sessionId,
    p_lock_minutes: LOCK_LEASE_MINUTES,
  });
  if (error) {
    console.error("[publishQueue] claim_publish_job RPC error:", error);
    return null;
  }
  if (!data || data.length === 0) return null;
  return data[0] as ClaimedPublishJob;
}

/**
 * Heartbeat: extend lock_expires_at for jobs currently locked by this session.
 */
async function heartbeatPublishLocks(sessionId: string, activeJobIds: Set<string>): Promise<void> {
  if (activeJobIds.size === 0) return;
  const newExpiry = new Date(Date.now() + LOCK_LEASE_MINUTES * 60 * 1000).toISOString();
  await (supabase as any)
    .from("publish_queue")
    .update({ lock_expires_at: newExpiry })
    .eq("locked_by", sessionId)
    .in("id", Array.from(activeJobIds));
}

/**
 * Cleanup lock fields after job reaches terminal state (and apply patch).
 */
async function clearPublishLock(jobId: string, patch: Record<string, unknown>): Promise<void> {
  await (supabase as any)
    .from("publish_queue")
    .update({
      ...patch,
      locked_by: null,
      locked_at: null,
      lock_expires_at: null,
    })
    .eq("id", jobId);
}

// =============================================
// Scheduling
// =============================================

/**
 * Calculate next available publish slot for a domain.
 * Respects: rate limit, time window (6:00-22:00 Warsaw), random delay.
 */
async function calculateNextSlot(websiteId: string): Promise<Date> {
  // Get last published article for this domain
  const { data: lastPublished } = await (supabase as any)
    .from("publish_queue")
    .select("published_at")
    .eq("website_id", websiteId)
    .eq("status", "published")
    .order("published_at", { ascending: false })
    .limit(1);

  // Get scheduled items for this domain (not yet published)
  const { data: scheduled } = await (supabase as any)
    .from("publish_queue")
    .select("scheduled_at")
    .eq("website_id", websiteId)
    .in("status", ["queued", "scheduled"])
    .order("scheduled_at", { ascending: false })
    .limit(1);

  // Base time: latest of last published or last scheduled
  const lastTime = scheduled?.[0]?.scheduled_at || lastPublished?.[0]?.published_at;

  let nextSlot: Date;

  if (!lastTime) {
    // First article — schedule for next valid window
    nextSlot = new Date();
    if (nextSlot.getHours() < PUBLISH_WINDOW_START) {
      nextSlot.setHours(PUBLISH_WINDOW_START, 0, 0, 0);
    } else if (nextSlot.getHours() >= PUBLISH_WINDOW_END) {
      nextSlot.setDate(nextSlot.getDate() + 1);
      nextSlot.setHours(PUBLISH_WINDOW_START, 0, 0, 0);
    }
  } else {
    // Add random delay (8-16 hours)
    const randomHours = MIN_DELAY_HOURS + Math.random() * (MAX_DELAY_HOURS - MIN_DELAY_HOURS);
    nextSlot = new Date(new Date(lastTime).getTime() + randomHours * 3600_000);
  }

  // Constrain to publish window
  if (nextSlot.getHours() < PUBLISH_WINDOW_START) {
    nextSlot.setHours(PUBLISH_WINDOW_START, Math.floor(Math.random() * 30), 0, 0);
  }
  if (nextSlot.getHours() >= PUBLISH_WINDOW_END) {
    nextSlot.setDate(nextSlot.getDate() + 1);
    nextSlot.setHours(PUBLISH_WINDOW_START, Math.floor(Math.random() * 30), 0, 0);
  }

  // Check daily rate limit for this domain
  const slotDate = new Date(nextSlot);
  slotDate.setHours(0, 0, 0, 0);
  const nextDay = new Date(slotDate);
  nextDay.setDate(nextDay.getDate() + 1);

  const { count } = await (supabase as any)
    .from("publish_queue")
    .select("id", { count: "exact", head: true })
    .eq("website_id", websiteId)
    .in("status", ["queued", "scheduled", "published"])
    .gte("scheduled_at", slotDate.toISOString())
    .lt("scheduled_at", nextDay.toISOString());

  if ((count || 0) >= PUBLISH_RATE_PER_DOMAIN) {
    // Move to next day
    nextSlot.setDate(nextSlot.getDate() + 1);
    nextSlot.setHours(
      PUBLISH_WINDOW_START + Math.floor(Math.random() * 4),
      Math.floor(Math.random() * 60),
      0,
      0,
    );
  }

  return nextSlot;
}

// =============================================
// Queue operations
// =============================================

export interface PublishStatus {
  queued: number;
  scheduled: number;
  publishing: number;
  published: number;
  failed: number;
}

/**
 * Get publish queue status.
 */
export async function getPublishStatus(mapId?: string): Promise<PublishStatus> {
  let query = (supabase as any).from("publish_queue").select("status");
  if (mapId) query = query.eq("map_id", mapId);

  const { data } = await query;
  const items = (data || []) as Array<{ status: string }>;

  return {
    queued: items.filter((i) => i.status === "queued").length,
    scheduled: items.filter((i) => i.status === "scheduled").length,
    publishing: items.filter((i) => i.status === "publishing").length,
    published: items.filter((i) => i.status === "published").length,
    failed: items.filter((i) => i.status === "failed").length,
  };
}

/**
 * Lightweight ctx dla post-audit backfill: tylko pola które audit faktycznie czyta
 * (boundaries_not_owned + required_entities + entities z aliases dla coverage check).
 * Świadomie pomijamy resztę NodeContentContext — audit ma fallbacki na brakujące pola.
 */
async function buildLightweightAuditCtx(nodeId: string): Promise<any> {
  const { data: node } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, map_id, required_entities")
    .eq("id", nodeId)
    .single();

  if (!node) {
    return {
      map: { boundaries_not_owned: [] },
      node: { required_entities: [] },
      knowledge: { entities: [] },
    };
  }

  const [mapRes, entitiesRes] = await Promise.all([
    (supabase as any)
      .from("thematic_maps")
      .select("boundaries_not_owned")
      .eq("id", node.map_id)
      .single(),
    (supabase as any)
      .from("thematic_entities")
      .select("canonical_name, aliases")
      .eq("map_id", node.map_id),
  ]);

  return {
    map: { boundaries_not_owned: mapRes.data?.boundaries_not_owned ?? [] },
    node: { required_entities: node.required_entities ?? [] },
    knowledge: { entities: entitiesRes.data ?? [] },
  };
}

/**
 * Add nodes to the publish queue with automatic scheduling.
 */
export async function addToPublishQueue(
  nodeIds: string[],
  mapId: string,
  websiteId: string,
): Promise<number> {
  // UWAGA: queue jest DB-side i dziala w 2 trybach processowania:
  //   - worker mode ON  → background container claimuje jobs przez BullMQ
  //   - worker mode OFF → browser tab (QueueDashboard polling loop) claimuje jobs
  // Enqueue jest ZAWSZE dozwolony — procesor wezmie gdy bedzie aktywny.
  const { data: session } = await supabase.auth.getSession();
  const userId = session?.session?.user?.id;

  let added = 0;

  for (const nodeId of nodeIds) {
    // Check node has ready content
    const { data: content } = await (supabase as any)
      .from("thematic_node_content")
      .select("final_html, engine_stage, qa_status, is_stale, needs_regeneration, published_at, wp_post_id")
      .eq("node_id", nodeId)
      .single();

    // Twarda blokada publikacji
    let qaStatus = (content?.qa_status ?? "pending");
    const isStale = (content?.is_stale ?? false);
    const needsRegen = (content?.needs_regeneration ?? false);

    if (!content?.final_html) {
      console.warn(`[Publish] Skip ${nodeId}: brak treści`);
      continue;
    }
    if (content.engine_stage !== "ready") {
      console.warn(`[Publish] Skip ${nodeId}: engine_stage=${content.engine_stage}`);
      continue;
    }

    // Lazy auto-QA backfill: dla nodów które przeszły przez pipeline ale audit nigdy
    // nie zapisał qa_status (np. deploy/restart w trakcie generacji). Audit jest
    // deterministyczny, 0$ LLM, <1s. Try/catch → fallback do dzisiejszego skip-pending.
    if (AUTO_QA_ON_ENQUEUE_ENABLED && qaStatus === "pending") {
      try {
        const auditCtx = await buildLightweightAuditCtx(nodeId);
        const audit = postContentAudit(content.final_html, auditCtx, {});
        await (supabase as any)
          .from("thematic_node_content")
          .update({
            qa_status: audit.qa_status,
            quality_score: audit.quality_score,
            qa_checked_at: new Date().toISOString(),
          })
          .eq("node_id", nodeId);
        console.log(
          `[Publish] Auto-QA backfill ${nodeId}: ${audit.qa_status} (score ${audit.quality_score}/100)`,
        );
        qaStatus = audit.qa_status;
      } catch (err) {
        console.warn(
          `[Publish] Auto-QA backfill failed for ${nodeId} — fallback do skip:`,
          err,
        );
      }
    }

    if (qaStatus !== "approved") {
      console.warn(`[Publish] Skip ${nodeId}: qa_status=${qaStatus}`);
      continue;
    }
    if (isStale === true) {
      console.warn(`[Publish] Skip ${nodeId}: treść nieaktualna`);
      continue;
    }
    if (needsRegen === true) {
      console.warn(`[Publish] Skip ${nodeId}: wymaga regeneracji`);
      continue;
    }
    if (content.published_at || content.wp_post_id) {
      console.warn(`[Publish] Skip ${nodeId}: już opublikowany`);
      continue;
    }

    // Check not already in publish queue
    const { data: existing } = await (supabase as any)
      .from("publish_queue")
      .select("id")
      .eq("node_id", nodeId)
      .in("status", ["queued", "scheduled", "publishing"])
      .limit(1);

    if (existing && existing.length > 0) continue;

    // Calculate schedule
    const scheduledAt = await calculateNextSlot(websiteId);

    await (supabase as any).from("publish_queue").insert({
      user_id: userId,
      map_id: mapId,
      node_id: nodeId,
      website_id: websiteId,
      status: "scheduled",
      scheduled_at: scheduledAt.toISOString(),
    });

    added++;
  }

  return added;
}

/**
 * Process publish queue: publish articles that are due.
 * Atomic claim via FOR UPDATE SKIP LOCKED (RPC) — 2 sessions never grab the same job.
 * Stale lock recovery: status='publishing' with lock_expires_at < NOW() get re-claimed.
 *
 * @param forceAll — if true, fast-forward all scheduled_at to NOW() so the claim loop
 *                   picks them up immediately (still honors locking + per-domain rate limits)
 */
export async function processPublishQueue(forceAll = false): Promise<{
  published: number;
  failed: number;
  skipped: number;
}> {
  // Worker Mode: delegate to server-side worker (no browser loop)
  if (await isWorkerModeEnabled()) {
    const { data: queued } = await (supabase as any)
      .from("publish_queue")
      .select("node_id, map_id, website_id")
      .in("status", ["queued", "scheduled"]);
    // Group by (map_id, website_id) — worker enqueue endpoint takes (mapId, websiteId, nodeIds)
    const buckets = new Map<string, string[]>();
    for (const row of (queued || []) as Array<{ node_id: string; map_id: string; website_id: string | null }>) {
      if (!row.website_id) continue;
      const bucketKey = `${row.map_id}::${row.website_id}`;
      const arr = buckets.get(bucketKey) || [];
      arr.push(row.node_id);
      buckets.set(bucketKey, arr);
    }
    let enqueued = 0;
    for (const [bucketKey, nodeIds] of buckets.entries()) {
      const [mapId, websiteId] = bucketKey.split("::");
      try {
        const res = await enqueuePublishJob(mapId, websiteId, nodeIds);
        enqueued += res.enqueued;
      } catch (err) {
        console.error("[publishQueue] worker enqueue failed:", err);
      }
    }
    return { published: enqueued, failed: 0, skipped: 0 };
  }

  // sessionId — unique per processPublishQueue() invocation. Used for lock ownership.
  const sessionId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // forceAll: fast-forward scheduled_at so RPC gate (scheduled_at <= NOW()) lets them through
  if (forceAll) {
    await (supabase as any)
      .from("publish_queue")
      .update({ scheduled_at: new Date().toISOString() })
      .in("status", ["queued", "scheduled"])
      .gt("scheduled_at", new Date().toISOString());
  }

  let published = 0;
  let failed = 0;
  let skipped = 0;

  const activeJobIds = new Set<string>();
  const heartbeatTimer = setInterval(() => {
    heartbeatPublishLocks(sessionId, activeJobIds).catch((e) =>
      console.error("[publishQueue] heartbeat failed:", e),
    );
  }, HEARTBEAT_INTERVAL_MS);

  try {
    while (true) {
      const item = await claimNextPublishJob(sessionId);
      if (!item) break; // nothing due / nothing left
      activeJobIds.add(item.id);

      try {
      // Get content (including featured image + backdated publish date)
      const { data: content } = await (supabase as any)
        .from("thematic_node_content")
        .select("final_html, seo_title, meta_description, featured_image_url, effective_publish_date")
        .eq("node_id", item.node_id)
        .single();

      if (!content?.final_html) {
        throw new Error("Brak final_html dla node");
      }

      // Get node metadata for tags + refurbish fields
      const { data: node } = await (supabase as any)
        .from("thematic_nodes")
        .select(
          "title, slug, primary_entity_raw, required_entities_raw, document_role, match_status, refurbish_approved_by_user, matched_wp_post_id, refurbish_image_strategy",
        )
        .eq("id", item.node_id)
        .single();

      // Get website credentials (FIX: use actual column names from websites table)
      const { data: website } = await (supabase as any)
        .from("websites")
        .select("url, login, password, api_key")
        .eq("id", item.website_id)
        .single();

      const wpUsername = website?.login;
      const wpPassword = website?.api_key || website?.password;
      if (!wpUsername || !wpPassword) {
        throw new Error("Brak danych WP dla domeny (login/api_key w tabeli websites)");
      }

      // Set WP credentials for this publish operation
      wordpressService.setCredentials({
        url: website.url.replace(/\/$/, ""),
        username: wpUsername,
        password: wpPassword,
      });

      // ===== M15 Decision Engine: final_action dominuje nad match_status =====
      // Gdy wp_post.final_action_locked=true -> switch na final_action.
      // Inaczej fallthrough do starego refurbish branch (kompatybilnosc wstecz).
      if (node?.matched_wp_post_id) {
        const { data: wpPost } = await (supabase as any)
          .from("wp_posts")
          .select("id, wp_post_id, title, wp_url, final_action, final_action_locked, final_action_reason")
          .eq("id", node.matched_wp_post_id)
          .maybeSingle();

        const fa = wpPost?.final_action as
          | "keep" | "refurbish" | "refurbish_url_preserve"
          | "keep_outer" | "delete" | "consolidate_301" | "monitor" | null
          | undefined;
        const faLocked = wpPost?.final_action_locked === true;

        if (faLocked && fa) {
          // Skip akcje (nie tworzymy ani nie updatujemy WP)
          if (fa === "keep" || fa === "keep_outer" || fa === "monitor") {
            await (supabase as any)
              .from("thematic_node_content")
              .update({
                engine_stage: "skipped_existing",
                engine_debug: {
                  skipped_by_final_action: fa,
                  reason: wpPost.final_action_reason,
                },
              })
              .eq("node_id", item.node_id);
            await clearPublishLock(item.id, { status: "skipped", error: `final_action=${fa}` });
            skipped++;
            continue;
          }

          // Refurbish akcje
          if (fa === "refurbish" || fa === "refurbish_url_preserve") {
            const { refurbishWpPost } = await import("./wpPostRefurbishService");
            const refurbishRes = await refurbishWpPost({
              nodeId: item.node_id,
              keepFeaturedImage: node.refurbish_image_strategy === "keep",
            });
            if (!refurbishRes.success) {
              throw new Error(refurbishRes.error || "Refurbish failed");
            }
            await clearPublishLock(item.id, {
              status: "published",
              wp_post_id: refurbishRes.wp_post_id,
              wp_post_url: refurbishRes.wp_url,
              published_at: new Date().toISOString(),
            });
            published++;
            continue;
          }

          // Delete — soft delete w WP + wp_posts_trash
          if (fa === "delete") {
            await wordpressService.softDeletePost(wpPost.wp_post_id);
            await (supabase as any).from("wp_posts_trash").insert({
              wp_post_id: wpPost.id,
              website_id: item.website_id,
              wp_remote_post_id: wpPost.wp_post_id,
              title: wpPost.title,
              wp_url: wpPost.wp_url,
              status_before_delete: "publish",
              soft_delete_reason: wpPost.final_action_reason,
            });
            await (supabase as any)
              .from("thematic_node_content")
              .update({
                engine_stage: "skipped_existing",
                engine_debug: {
                  skipped_by_final_action: "delete",
                  reason: wpPost.final_action_reason,
                },
              })
              .eq("node_id", item.node_id);
            await clearPublishLock(item.id, { status: "skipped", error: "soft_deleted" });
            skipped++;
            continue;
          }

          // Consolidate 301 — pending user decision (winner/loser)
          if (fa === "consolidate_301") {
            await clearPublishLock(item.id, {
              status: "skipped",
              error: "consolidate_301 pending user decision",
            });
            skipped++;
            continue;
          }
        }
      }
      // ===== END M15 Decision Engine branch =====

      // ===== Existing Posts Awareness: refurbish branch (fallback gdy !locked) =====
      // Jesli node jest matched_refurbish + user zatwierdzil + ma wp_post_id:
      // UPDATE istniejacego wpisu (ten sam URL) zamiast CREATE nowego.
      if (
        node?.match_status === "matched_refurbish" &&
        node?.refurbish_approved_by_user &&
        node?.matched_wp_post_id
      ) {
        const { refurbishWpPost } = await import("./wpPostRefurbishService");
        const refurbishRes = await refurbishWpPost({
          nodeId: item.node_id,
          keepFeaturedImage: node.refurbish_image_strategy === "keep",
        });

        if (!refurbishRes.success) {
          throw new Error(refurbishRes.error || "Refurbish failed");
        }

        // refurbishWpPost juz updatuje thematic_node_content.engine_stage='published',
        // thematic_nodes.refurbished_at, wp_posts. Tu updatujemy tylko publish_queue.
        await clearPublishLock(item.id, {
          status: "published",
          wp_post_id: refurbishRes.wp_post_id,
          wp_post_url: refurbishRes.wp_url,
          published_at: new Date().toISOString(),
        });

        published++;
        continue; // pomijamy standardowy flow (image gen + createPost)
      }
      // ===== END refurbish branch =====

      // Auto-generate featured image if missing
      let featuredImageUrl = content.featured_image_url;
      if (!featuredImageUrl) {
        console.log(`[Publish] No featured image for node=${item.node_id?.slice(0, 8)} — auto-generating...`);
        const { generateFeaturedImage } = await import("@/services/imageGeneratorService");
        const imgResult = await generateFeaturedImage(item.node_id, item.map_id);
        featuredImageUrl = imgResult?.url || null;
      }

      // Upload featured image to WP Media Library
      let wpMediaId: number | undefined;
      if (featuredImageUrl) {
        console.log(`[Publish] Uploading featured image to WP Media Library...`);
        const { fetchImageAsFile } = await import("@/services/imageGeneratorService");
        const slug = node?.slug || item.node_id.slice(0, 8);
        const imageFile = await fetchImageAsFile(featuredImageUrl, `featured-${slug}`);
        if (imageFile) {
          const uploadResult = await wordpressService.uploadMedia(imageFile, imageFile.name);
          if (uploadResult.success && uploadResult.data?.id) {
            wpMediaId = uploadResult.data.id;
            // Update alt text with SEO title
            const altText = content.seo_title || node?.title || "";
            if (altText) {
              await wordpressService.updateMediaAltText(wpMediaId, altText);
            }
            console.log(`[Publish] Image uploaded: media_id=${wpMediaId}`);
          } else {
            console.warn(`[Publish] Image upload failed:`, uploadResult.error);
          }
        }
      }

      // HTML cleanup PRZED dalszym processingiem:
      // 1. Usun duplikat H1 (jesli pierwszy H2 ma identyczny tekst co H1 → usun H1)
      // 2. Usun puste paragrafy/divy miedzy naglowkami
      const wpTitle = (content.seo_title || node?.title || "").trim().toLowerCase();
      const cleanupHtml = (html: string): string => {
        if (typeof DOMParser === "undefined") return html;
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(`<body>${html}</body>`, "text/html");
          const body = doc.body;

          // 1a. Bezwarunkowo usun WSZYSTKIE H1 z body — WP renderuje post.title
          //     jako H1 osobno w theme, wiec H1 w content = duplikat.
          body.querySelectorAll("h1").forEach((h1) => h1.remove());

          // 1c. Usun <figcaption> ze wszystkich figure (user nie chce — psuje wizualnie).
          //     alt_text zostaje na <img alt=""> dla SEO + accessibility.
          body.querySelectorAll("figcaption").forEach((fc) => fc.remove());

          // 1b. Jesli pierwszy H2 matchuje wpTitle → tez duplikat, usun.
          //     (Czasem content rozpoczyna sie od H2 z tytulem zamiast H1.)
          const firstH2 = body.querySelector("h2");
          if (firstH2 && wpTitle) {
            const h2Text = (firstH2.textContent || "").trim().toLowerCase();
            if (h2Text === wpTitle) {
              firstH2.remove();
            }
          }

          // 2. Puste/whitespace-only paragrafy
          const emptyPs = body.querySelectorAll("p");
          emptyPs.forEach((p) => {
            const txt = (p.textContent || "").trim();
            const hasMedia = p.querySelector("img, figure, iframe, video");
            if (!txt && !hasMedia) p.remove();
          });

          // 3. Puste <br> consecutive (>1 z rzedu) — keep only one
          const brs = body.querySelectorAll("br + br");
          brs.forEach((br) => br.remove());

          return body.innerHTML;
        } catch {
          return html;
        }
      };

      // In-content images — LOSOWE 0-3 obrazkow nawiazujacych do tresci sekcji.
      // OPT-IN: domyslnie WYLACZONE. User musi zaznaczyc checkbox w QueueDashboard
      // (localStorage key "enable_in_content_images"). Browser-only flow wiec
      // localStorage zawsze dostepne w publish flow.
      let finalContent = cleanupHtml(content.final_html);

      // STOP-2 link-resolver — pre-publish strip pending + normalize live.
      // Forward-only: dotyczy TYLKO tego konkretnie publikowanego węzła.
      // - live target (ma wp_post_url) → <a href="${wp_post_url}"> (kanoniczny)
      // - pending target (brak wp_post_url) → <span data-pending-link="/slug"> (inert marker)
      // DB final_html UPDATEowany żeby heal job (STOP-3) miał source of truth.
      // Non-blocking: try/catch — błąd resolvera nie blokuje publish.
      try {
        const { data: linkMapRows } = await (supabase as any)
          .from("thematic_nodes")
          .select("id, slug")
          .eq("map_id", item.map_id);
        if (linkMapRows && linkMapRows.length > 0) {
          const nodeIdsInMap = linkMapRows.map((n: { id: string }) => n.id);
          const { data: contentRows } = await (supabase as any)
            .from("thematic_node_content")
            .select("node_id, wp_post_url")
            .in("node_id", nodeIdsInMap);
          const wpUrlByNodeId = new Map<string, string | null>();
          for (const c of contentRows || []) {
            wpUrlByNodeId.set(
              c.node_id,
              c.wp_post_url && String(c.wp_post_url).trim()
                ? String(c.wp_post_url)
                : null,
            );
          }
          const slugToWpUrl = new Map<string, string | null>();
          for (const n of linkMapRows as Array<{ id: string; slug: string }>) {
            if (n.slug) {
              slugToWpUrl.set(n.slug, wpUrlByNodeId.get(n.id) ?? null);
            }
          }
          const resolved = stripPendingLinks(
            finalContent,
            slugToWpUrl,
            node?.slug ?? null,
          );
          if (resolved.html !== finalContent) {
            // Update DB jako source of truth (heal job query'uje DB final_html)
            await (supabase as any)
              .from("thematic_node_content")
              .update({ final_html: resolved.html })
              .eq("node_id", item.node_id);
            finalContent = resolved.html;
            logPublic(
              "LINK_RESOLVER",
              `node ${item.node_id?.slice(0, 8)}: ${resolved.normalizedToAbsolute.length} normalized, ${resolved.pendingMarkers.length} marked pending`,
            );
          }
        }
      } catch (e) {
        logPublic(
          "LINK_RESOLVER",
          `strip failed (non-blocking) for ${item.node_id?.slice(0, 8)}: ${e instanceof Error ? e.message : String(e)}`,
        );
      }

      const inContentEnabled =
        typeof window !== "undefined" &&
        window.localStorage?.getItem("enable_in_content_images") === "true";
      if (!inContentEnabled) {
        logPublic("IMAGES", "In-content: WYLACZONE (default OFF — zaznacz checkbox w QueueDashboard zeby wlaczyc).");
      }
      try {
        if (!inContentEnabled) {
          // Skip caly blok — finalContent zostaje z samego cleanupHtml.
          throw new Error("__skip_in_content_disabled");
        }
        const { addInContentImages } = await import(
          "@/services/contentEngine/inContentImagesService"
        );
        // Pobierz outline z DB (potrzebny dla LLM planner — sections context)
        const { data: contentRow } = await (supabase as any)
          .from("thematic_node_content")
          .select("outline")
          .eq("node_id", item.node_id)
          .maybeSingle();
        const { data: mapRow } = await (supabase as any)
          .from("thematic_maps")
          .select("language")
          .eq("id", item.map_id)
          .maybeSingle();
        const outlineSections = Array.isArray(contentRow?.outline?.sections)
          ? contentRow.outline.sections.map((s: any) => ({
              heading: s.heading || s.h2 || "",
              type: s.type,
              word_count: s.word_count,
            }))
          : [];
        const language = mapRow?.language || "pl";

        if (outlineSections.length >= 2) {
          const imagesResult = await addInContentImages(
            finalContent,
            outlineSections,
            language,
            item.node_id,
            item.map_id,
          );
          if (imagesResult.imagesAdded > 0) {
            finalContent = imagesResult.finalHtml;
            logPublic(
              "IMAGES",
              `In-content: ${imagesResult.imagesAdded} obrazkow dodano${imagesResult.imagesFailed > 0 ? `, ${imagesResult.imagesFailed} fail` : ""} — $${imagesResult.costUsd.toFixed(3)}`,
            );
            // UWAGA: client-side upload do WP Media + WebP konwersja byly tu, ale
            // padaja na CORS preflight (growthhacker.pl /wp-json/wp/v2/media nie ma
            // Access-Control-Allow-Origin dla naszej domeny) + CSP (blob: URLs blokowane
            // przez img-src 'self' data: https:). Hotlink fal.media URLs zostaje —
            // dziala out-of-box, WP zaakceptuje external <img src>.
            // TODO: server-side upload via Supabase Edge Function lub worker, gdy worker
            // wroci do produkcji. Wtedy CORS niepotrzebny.
          } else {
            logPublic(
              "IMAGES",
              `In-content: 0 obrazkow (LLM plan empty lub all failed). Publish bez in-content.`,
            );
          }
        } else {
          logPublic(
            "IMAGES",
            `In-content: outline za krotki (${outlineSections.length} sekcji), skip.`,
          );
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg === "__skip_in_content_disabled") {
          // Intencjonalne skip przez user toggle — juz zalogowane wczesniej, NIE log error.
        } else {
          logPublic("IMAGES", `In-content failed (non-blocking): ${msg}`);
        }
      }

      // Schema.org JSON-LD injection — publish-time only, zero wpływu na Etap 1/2
      const sizeBefore = finalContent.length;
      let schemaPayload: Awaited<ReturnType<typeof generateSchemasForNode>> | null = null;

      try {
        schemaPayload = await generateSchemasForNode(item.node_id);
        const validation = validateSchemaPayload(schemaPayload);

        if (validation.hasBlockingErrors) {
          logPublic(
            "SCHEMA",
            `BLOCKED publish for node ${item.node_id}: ${validation.errors.join("; ")}`,
          );
          throw new Error(`Schema blocking errors: ${validation.errors.join("; ")}`);
        }

        if (validation.warnings.length > 0) {
          logPublic("SCHEMA", `WARN node ${item.node_id}: ${validation.warnings.join("; ")}`);
        }

        if (schemaPayload.graph && schemaPayload.graph["@graph"].length > 0) {
          finalContent = injectSchemasIntoHtml(finalContent, schemaPayload.graph);
          logPublic(
            "SCHEMA",
            `Injected @graph with ${schemaPayload.graph["@graph"].length} node(s) for node ${item.node_id}`,
          );
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes("Schema blocking errors")) throw e;
        logPublic("SCHEMA", `Generation failed (non-blocking) for node ${item.node_id}: ${msg}`);
      }

      // Auto-resolve seed → WP category (zero UI, zero akcji uzera).
      // Flow:
      //   1. Czy mapping juz w DB? → use cached wp_category_id
      //   2. Nie? → createCategoryForSeed (REUSE WP cat o tym slugu LUB utworz)
      //      → cache do DB dla nastepnych publishy
      //   3. Kazdy publish ma kategorie zgodna z seedem automatycznie.
      let wpCategoryId: number | null = null;
      try {
        const { getCategoryIdForNode, createCategoryForSeed } = await import(
          "./seedCategoryMappingService"
        );
        wpCategoryId = await getCategoryIdForNode(item.node_id, item.website_id);
        if (wpCategoryId === null) {
          // Brak mapping → auto-create
          const { data: nodeRow } = await (supabase as any)
            .from("thematic_nodes")
            .select("seed_id")
            .eq("id", item.node_id)
            .maybeSingle();
          if (nodeRow?.seed_id) {
            logPublic(
              "CATEGORY",
              `Brak mapping dla seed ${nodeRow.seed_id} — auto-tworze kategorie w WP...`,
            );
            const createRes = await createCategoryForSeed(nodeRow.seed_id, item.website_id);
            if (createRes.success) {
              wpCategoryId = await getCategoryIdForNode(item.node_id, item.website_id);
              logPublic(
                "CATEGORY",
                `Auto-create: ${createRes.created ? "utworzono nowa WP cat" : "reuse existing WP cat"} → category #${wpCategoryId}`,
              );
            } else {
              logPublic(
                "CATEGORY",
                `WARN auto-create failed dla seed ${nodeRow.seed_id}: ${createRes.error}. Publish bez kategorii.`,
              );
            }
          } else {
            logPublic("CATEGORY", `Node ${item.node_id} bez seed_id — publish bez kategorii`);
          }
        }
      } catch (e) {
        logPublic(
          "CATEGORY",
          `WARN resolve wp_category_id failed for node ${item.node_id}: ${e instanceof Error ? e.message : String(e)}`,
        );
      }

      // Publish to WordPress.
      // FIX 2026-05-18: WP post title = ZAWSZE oryginalny H1 (node.title).
      // seo_title z generatora idzie do:
      //   - schema.org headline (już używa content.seo_title fallback do node.title)
      //   - Yoast SEO title (meta._yoast_wpseo_title — osobne pole, <title> w head)
      // post.title (H1 na stronie) NIE jest nadpisywany — zostaje oryginalny tytuł.
      // status: "publish" — od razu live na blogu (zmiana z "draft" 2026-05-18).
      // Powód: user wrzuca partiami 40 wpisów, dopinają historyczne daty backdate
      // i publikuje wszystko za jednym kliknięciem. Draft → niepotrzebny manual step.
      const wpPost: Record<string, unknown> = {
        title: node?.title || content.seo_title || "Untitled",
        content: finalContent,
        status: "publish" as const,
        slug: node?.slug || undefined,
      };

      // Backdated publish date — schema.org datePublished + WP post.date_gmt synchronicznie.
      // Gdy effective_publish_date set → użyj go (naturalna historia bloga).
      // Gdy NULL → WP defaultuje do NOW (klasyczny flow, zero regresji).
      if (content.effective_publish_date) {
        wpPost.date_gmt = content.effective_publish_date;
        wpPost.date = content.effective_publish_date;
      }
      // Yoast SEO meta — osobne od post.title. Yoast 14+ obsługuje przez REST.
      const yoastMeta: Record<string, string> = {};
      if (content.seo_title) yoastMeta._yoast_wpseo_title = content.seo_title;
      if (content.meta_description) yoastMeta._yoast_wpseo_metadesc = content.meta_description;
      if (Object.keys(yoastMeta).length > 0) {
        wpPost.meta = yoastMeta;
        logPublic(
          "SEO_META",
          `WP meta: yoast title=${(content.seo_title || "").length}ch, metadesc=${(content.meta_description || "").length}ch`,
        );
      }
      if (wpMediaId) {
        wpPost.featured_media = wpMediaId;
      }
      if (wpCategoryId) {
        wpPost.categories = [wpCategoryId];
        logPublic("CATEGORY", `Node ${item.node_id} → WP category #${wpCategoryId}`);
      }

      const result = await wordpressService.createPost(wpPost);

      if (!result.success) {
        throw new Error(result.error || "WordPress publish failed");
      }

      // wordpressService.createPost zwraca { postId, url, featured_media } w data.
      // Wcześniej tu było result.data?.id — NIE istnieje, zawsze undefined → wp_post_id zapisywany jako NULL.
      // Stąd audit + revert + refurbish nie mogły pobrać posta przez REST.
      const wpPostId = (result.data as { postId?: number; id?: number })?.postId
        ?? (result.data as { id?: number })?.id;
      const wpPostUrl =
        (result.data as { url?: string; link?: string })?.url
        || (result.data as { link?: string })?.link
        || "";

      // Audit log schema injection (po publish — mamy wpPostId)
      if (schemaPayload && schemaPayload.graph && schemaPayload.graph["@graph"].length > 0) {
        await logSchemaInjection(
          item.node_id,
          schemaPayload,
          sizeBefore,
          finalContent.length,
          typeof wpPostId === "number" ? wpPostId : undefined,
        );
      }

      // published_at = effective_publish_date (backdated) lub NOW (fallback).
      // Spójność: DB published_at == WP post.date_gmt == schema.datePublished.
      const effectivePublishedAt = content.effective_publish_date || new Date().toISOString();

      // Update publish queue
      await clearPublishLock(item.id, {
        status: "published",
        wp_post_id: wpPostId,
        wp_post_url: wpPostUrl,
        published_at: effectivePublishedAt,
      });

      // Update node content
      await (supabase as any)
        .from("thematic_node_content")
        .update({
          published_at: effectivePublishedAt,
          wp_post_id: wpPostId,
          wp_post_url: wpPostUrl,
          engine_stage: "published",
        })
        .eq("node_id", item.node_id);

      // Update node status
      await (supabase as any)
        .from("thematic_nodes")
        .update({ node_status: "published" })
        .eq("id", item.node_id);

      // STOP-3 link-resolver — heal pending links wskazujące na nowo opublikowany node.
      // Fire-and-forget: błąd loguje, nie blokuje publish (sukces już ustawiony powyżej).
      // Scoped per mapId, idempotentny, działa TYLKO na marker data-pending-link.
      (async () => {
        try {
          const r = await healPendingLinksForNewPublish(
            item.node_id,
            item.map_id,
          );
          if (r && r.linksHealed > 0) {
            logPublic(
              "HEAL",
              `Auto-heal po publish ${item.node_id?.slice(0, 8)}: ${r.linksHealed} linków zhealowane w ${r.sourcesUpdated.length} wpisach`,
            );
          }
        } catch (e) {
          logPublic(
            "HEAL",
            `heal failed (non-blocking) dla ${item.node_id?.slice(0, 8)}: ${e instanceof Error ? e.message : String(e)}`,
          );
        }
      })();

      // Auto-trigger basic audit (Layers 1-5 only, FREE) — fire-and-forget.
      // LLM (Layer 6) opt-in przez UI w PublishedQAPanel — koszt nie auto-naliczany.
      // Failure NIE wpływa na publish success.
      (async () => {
        try {
          const { auditNode } = await import("@/services/contentAuditService");
          await auditNode(item.node_id, { includeLLM: false });
        } catch (e) {
          logPublic(
            "AUDIT_AUTO",
            `Auto-audit po publish failed dla node ${item.node_id}: ${e instanceof Error ? e.message : "unknown"}`,
          );
        }
      })();

      published++;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Unknown error";
        const retryCount = (item.retry_count || 0) + 1;

        await clearPublishLock(item.id, {
          status: retryCount >= MAX_RETRIES ? "failed" : "scheduled",
          error: errorMsg,
          retry_count: retryCount,
          // Reschedule in 1 hour if retrying
          ...(retryCount < MAX_RETRIES
            ? { scheduled_at: new Date(Date.now() + 3600_000).toISOString() }
            : {}),
        });

        failed++;
      } finally {
        activeJobIds.delete(item.id);
      }
    }
  } finally {
    clearInterval(heartbeatTimer);
  }

  return { published, failed, skipped };
}

/**
 * Cancel a scheduled publish (also clears lock fields if present).
 */
export async function cancelScheduled(queueId: string): Promise<void> {
  await (supabase as any)
    .from("publish_queue")
    .update({
      status: "cancelled",
      locked_by: null,
      locked_at: null,
      lock_expires_at: null,
    })
    .eq("id", queueId);
}

/**
 * Clear publish queue history — usuwa terminal rows (cancelled + failed) z tabeli.
 * NIE rusza published (live posts, audit trail) ani aktywnych (queued/scheduled/publishing).
 *
 * Use case: po revertach kolejka puchnie cancelled rowsami — UI cleanup.
 */
export async function clearPublishQueueHistory(mapId: string): Promise<number> {
  const { data, error } = await (supabase as any)
    .from("publish_queue")
    .delete()
    .eq("map_id", mapId)
    .in("status", ["cancelled", "failed"])
    .select("id");
  if (error) {
    console.error("[publishQueue] clearHistory failed:", error);
    return 0;
  }
  return ((data as Array<{ id: string }>) || []).length;
}

/**
 * Revert publication — cofa publikację artykulu.
 *
 * Co robi (sekwencyjnie, safe):
 * 1. WP side: post wylatuje do WP trash (status='trash') — NIE hard delete,
 *    można odzyskać w WP Admin → Posts → Trash przez 30 dni.
 * 2. DB side (thematic_node_content):
 *    - engine_stage → 'ready' (aby znów pojawił się przycisk Publikuj)
 *    - published_at, wp_post_id, wp_post_url → NULL
 *    - featured_image_url → NULL jesli options.clearImage=true (wymusi regenerację)
 * 3. publish_queue: wszystkie rows tego node (published/completed) → status='reverted'
 * 4. thematic_nodes.node_status → 'ready' (jesli byl 'published')
 *
 * NIE tyka: schema_injection_logs (zostają jako audit trail historii publikacji).
 *
 * @param nodeId — node UUID
 * @param options.clearImage — true (default): wymusza regenerację featured image
 *                            przy następnej publikacji. false: zachowuje URL.
 */
export async function revertPublication(
  nodeId: string,
  options: { clearImage?: boolean } = {},
): Promise<{ wpTrashed: boolean; dbReset: boolean; error?: string }> {
  const clearImage = options.clearImage !== false; // default true

  // 1. Fetch current state
  const { data: content } = await (supabase as any)
    .from("thematic_node_content")
    .select("wp_post_id, engine_stage, published_at")
    .eq("node_id", nodeId)
    .maybeSingle();

  if (!content) {
    return { wpTrashed: false, dbReset: false, error: "Node content not found" };
  }
  if (!content.published_at && !content.wp_post_id) {
    return { wpTrashed: false, dbReset: false, error: "Node nie został opublikowany — nic do cofnięcia" };
  }

  // 2. Pobierz wp credentials dla domeny
  const { data: nodeMap } = await (supabase as any)
    .from("thematic_nodes")
    .select("map_id")
    .eq("id", nodeId)
    .maybeSingle();
  const { data: map } = nodeMap?.map_id
    ? await (supabase as any).from("thematic_maps").select("website_id").eq("id", nodeMap.map_id).maybeSingle()
    : { data: null };
  const { data: website } = map?.website_id
    ? await (supabase as any).from("websites").select("url, login, api_key").eq("id", map.website_id).maybeSingle()
    : { data: null };

  // 3. WP trash (jeśli jest wp_post_id + website)
  let wpTrashed = false;
  if (content.wp_post_id && website?.url && website?.login && website?.api_key) {
    try {
      // FIX: setCredentials (NIE setConfig — to byl bug pre-existing, funkcja padala
      // na "setConfig is not a function" wiec revert nigdy nie dzialal poprzednio)
      wordpressService.setCredentials({
        url: website.url,
        username: website.login,
        password: website.api_key,
      });
      const res = await wordpressService.updatePost(content.wp_post_id, { status: "draft" });
      // 'draft' = ukryty ale trwały (vs 'trash' z 30d auto-delete w niektórych WP).
      // Jesli WP zwraca 404 (user juz wywalil) — kontynuuj reset DB, NIE blokuj.
      wpTrashed = res.success;
      if (!res.success) {
        console.warn(`[revertPublication] WP update failed: ${res.error}. Kontynuuje reset DB.`);
      }
    } catch (e) {
      console.warn(`[revertPublication] WP call threw: ${e instanceof Error ? e.message : String(e)}. Kontynuuje reset DB.`);
    }
  }

  // 4. Reset DB state
  const contentUpdate: Record<string, unknown> = {
    engine_stage: "ready",
    published_at: null,
    wp_post_id: null,
    wp_post_url: null,
  };
  if (clearImage) {
    contentUpdate.featured_image_url = null;
  }
  await (supabase as any).from("thematic_node_content").update(contentUpdate).eq("node_id", nodeId);

  // 5. Publish queue rows → 'cancelled' z error note (audit trail przez error column).
  // CHECK constraint dopuszcza tylko: queued|scheduled|publishing|published|failed|cancelled.
  await (supabase as any)
    .from("publish_queue")
    .update({
      status: "cancelled",
      error: "Reverted by user at " + new Date().toISOString(),
      locked_by: null,
      locked_at: null,
      lock_expires_at: null,
    })
    .eq("node_id", nodeId)
    .in("status", ["published", "completed"]);

  // 6. node_status reset (jesli było 'published' → wraca do 'draft').
  // CHECK constraint: planned|draft|published|refresh|deprecated
  await (supabase as any)
    .from("thematic_nodes")
    .update({ node_status: "draft" })
    .eq("id", nodeId)
    .eq("node_status", "published");

  return { wpTrashed, dbReset: true };
}

// =============================================================================
// (USUNIETE) In-content images upload do WP Media + WebP conversion
// =============================================================================
// Powod: client-side fetch + multipart upload do WP REST API padaja na CORS
// (growthhacker.pl /wp-json/wp/v2/media nie ma Access-Control-Allow-Origin dla
// dashboard.pbnsd666.website). Plus canvas conversion tworzy blob: URLs ktore
// nasza CSP "img-src 'self' data: https:" blokuje.
//
// TODO przyszlosc: server-side upload (Supabase Edge Function lub worker),
// wtedy CORS irrelevant. Na razie obrazki sa hot-linkowane z fal.media (URLs
// persistent, public, CDN — WP accepts external <img src> bez problemow).
// =============================================================================
